import React from 'react';

import { type ClassValue } from 'clsx';
import { useFormContext } from 'react-hook-form';

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import { isNullOrEmpty } from '@/services/helper';

type TInputTextAreaControl = {
	name: string,
	label?: string,
	className?: ClassValue,
	placeholder?: string,
	onChangeListen?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
	disabled?: boolean,
	hint?: React.ReactNode
} & StrictOmit<React.ComponentProps<'textarea'>, 'className'>;

const InputTextAreaControl = ({
	name,
	label,
	className,
	placeholder,
	onChangeListen,
	disabled = false,
	hint,
	...props
}: TInputTextAreaControl) => {
	const { control } = useFormContext();

	return (
		<FormField
			control={control}
			name={name}
			render={({
				field: { onChange, value, onBlur },
				formState: { disabled: formStateDisabled },
				fieldState: { error }
			}) => (
				<FormItem className={cn('flex w-full flex-col gap-2', className)}>
					{label ? (
						<FormLabel htmlFor={name}>
							{label} {props.required ? <span className="text-red">*</span> : null}
						</FormLabel>
					) : null}
					<FormControl>
						<div className="flex w-full flex-col">
							<textarea
								onBlur={onBlur}
								className={cn(
									'placeholder:text-placeholder selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex w-full min-w-0 rounded-md border-2 bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-gray-200 disabled:opacity-50 md:text-sm',
									'focus-visible:border-ring focus-visible:ring-primary focus-visible:ring-[1px]',
									'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive resize-none',
									className,
									{
										'border border-red-500': !isNullOrEmpty(error)
									}
								)}
								onChange={(e) => {
									if (onChangeListen) {
										onChangeListen(e);
									}
									onChange(e);
								}}
								value={value}
								name={name}
								placeholder={placeholder || label}
								disabled={disabled || formStateDisabled}
								id={name}
								{...props}
							/>
							<FormMessage className="mt-0" />
							{hint && <FormDescription className="mt-1">{hint}</FormDescription>}
						</div>
					</FormControl>
				</FormItem>
			)}
		/>
	);
};

export default InputTextAreaControl;
